import React,{ createContext, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import * as sessionsApi from "../services/auth";

const AuthContext = createContext({});

export const resolveNavigationPath = ({ user, current_club, current_organization, managed_clubs_count, organizations_count }) => {
  if (!user) return "/club_admin/login";

  if (!current_club && !current_organization && !managed_clubs_count && !organizations_count)
    return "/club_admin/no_access";

  if (current_club && !current_organization && !organizations_count)
    return "/club_admin/club";

  if (!current_club && managed_clubs_count > 0 && !organizations_count)
    return "/club_admin/clubs";

  if (current_organization && current_club)
    return "/club_admin/club";

  if (current_organization && !current_club)
    return "/club_admin/clubs_organization";

  if (!current_organization && organizations_count)
    return "/club_admin/organizations";

  return "/club_admin/no_access";
};

// Export the provider as we need to wrap the entire app with it
export function AuthProvider({ children }) {
  const [user, setUser] = useState();
  const [club, setClub] = useState();
  const [organization, setOrganization] = useState();
  const [organizations, setOrganizations] = useState();
  const [clubsCount, setClubsCount] = useState();
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();

  // If we change page, reset the error state.
   useEffect(() => {
    if (error) setError(null);
  }, [location.pathname]);

  const fetchUserData = async () => {
  try {
    const response = await sessionsApi.getCurrentUser();
    const { user, current_club, current_organization, organizations_count, managed_clubs_count } = response;

    setUser(user);
    setClub(current_club);
    setOrganization(current_organization);
    setOrganizations(organizations_count);
    setClubsCount(managed_clubs_count);

    return response;
  } catch (error) {
    
  } finally {
    setLoadingInitial(false);
  }
 };

  useEffect(() => {
    fetchUserData();
  }, []);

  const login =  async ({ email, password }) => {
      setLoading(true);
      setError(null);
      try {
        await sessionsApi.login({ email, password });
        const  userData = await fetchUserData();
        if (userData) {
         const path = resolveNavigationPath(userData);
         navigate(path);
        }
      } catch (error) {
        const errors = error?.response?.data?.errors;
        setError(errors?.length ? errors.map((e) => e?.message).join(", ") : 'Error occurred');
      } finally {
        setLoading(false);
      }
  };

  const logout = () => {
    sessionsApi.logout().then(() => {
      setUser(undefined);
      setClub(undefined);
      setOrganization(undefined);
      navigate("/club_admin/login")
    });
  };

  const forgotPassword = async ({ email }) => {
    setLoading(true);
    setError(null);
    try {
      const response = await sessionsApi.forgotPassword({ email });
      
    } catch (error) {
      const errors = error?.response?.data?.errors;
      setError(errors?.length ? errors.map((e) => e?.message).join(", ") : 'Error occurred');
    } finally {
      setLoading(false);
    }
  }

  const memoedValue = useMemo(
    () => ({
      user, 
      club, 
      organization, 
      organizations, 
      clubsCount,
      error, 
      loading, 
      login, 
      logout, 
      setClub: fetchUserData, 
      setOrganization: fetchUserData,
      forgotPassword,
    }),
    [user, club, organization, organizations, error, loading]
  );
  return  <AuthContext.Provider value={memoedValue}> {!loadingInitial && children} </AuthContext.Provider>;
};


export default function useAuthContext() {
  return useContext(AuthContext);
}
